import { IonContent, IonPage, useIonToast, useIonAlert } from '@ionic/react'
import { Redirect, useHistory, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { gql, useMutation } from '@apollo/client'
import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import CircularProgress from '@mui/material/CircularProgress'
import UpgradeIcon from '@mui/icons-material/Upgrade'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import StarIcon from '@mui/icons-material/Star'
import { LOGIN_PATH, SETTINGS_UPGRADE_PLAN_PATH, SETTINGS_PATH, SETTINGS_PLAN_PATH } from '../../../path'
import { TitleHeader } from '../../../components/Header'
import { DescriptionBox, is_upgradable, is_paid } from '../Component'
import Toast from '../../../utils/Toast'
import { useUser, get_plan } from '../../../data/user'
import { useTowers } from '../../../data/tower'
import { useMembers } from '../../../data/team'

const MasterDiv = styled.div`
	padding:20px 26px 40px 26px;

	& .title-header {
		display: grid;
		grid-template-columns: auto 140px;
		& .top-title {
			font-weight:bold;
			text-transform:capitalize;
		}
	}

	& .all-plans-section {
		margin-top: 40px;
		padding-top: 30px;
		border-top: 2px solid #e8e8e8;
	}

	& .section-title {
		font-size: 20px;
		font-weight: 700;
		margin-bottom: 8px;
		color: #1a1a1a;
	}

	& .intro-text {
		text-align: center;
		margin-bottom: 24px;
		color: #666;
		font-size: 14px;
		line-height: 1.6;
	}

	& .comparison-container {
		display: grid;
		grid-template-columns: 1fr;
		gap: 16px;
		margin-top: 20px;

		@media (min-width: 768px) {
			grid-template-columns: repeat(2, 1fr);
		}

		@media (min-width: 1024px) {
			grid-template-columns: repeat(4, 1fr);
		}
	}

	& .plan-card {
		background: white;
		border: 2px solid #e0e0e0;
		border-radius: 12px;
		padding: 18px;
		position: relative;
		transition: all 0.3s ease;
		display: flex;
		flex-direction: column;

		&:hover {
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
			transform: translateY(-2px);
		}

		&.current-plan {
			border-color: var(--mu-primary-main);
			background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%);
			box-shadow: 0 4px 16px rgba(23, 103, 193, 0.15);
		}
	}

	& .current-badge {
		position: absolute;
		top: 10px;
		right: 10px;
		background: var(--mu-primary-main);
		color: white;
		padding: 4px 10px;
		border-radius: 20px;
		font-size: 10px;
		font-weight: 600;
		display: flex;
		align-items: center;
		gap: 3px;
		text-transform: uppercase;
		letter-spacing: 0.5px;

		& svg {
			font-size: 13px;
		}
	}

	& .plan-header {
		margin-bottom: 16px;
		padding-bottom: 16px;
		border-bottom: 1px solid #e8e8e8;
	}

	& .plan-name {
		font-size: 20px;
		font-weight: 700;
		text-transform: capitalize;
		color: #1a1a1a;
		margin-bottom: 6px;
	}

	& .plan-price {
		font-size: 28px;
		font-weight: 700;
		color: var(--mu-primary-dark);
		margin-bottom: 4px;

		& .currency {
			font-size: 16px;
			font-weight: 500;
		}

		& .period {
			font-size: 13px;
			font-weight: 400;
			color: #666;
		}

		&.free {
			color: #666;
			font-size: 18px;
		}

		&.contact {
			font-size: 16px;
			font-weight: 600;
			color: #666;
		}
	}

	& .plan-gst {
		font-size: 11px;
		color: #888;
		font-style: italic;
	}

	& .plan-features {
		flex: 1;
		margin-bottom: 16px;
	}

	& .feature-group {
		margin-bottom: 14px;

		&:last-child {
			margin-bottom: 0;
		}
	}

	& .feature-group-title {
		font-size: 12px;
		font-weight: 600;
		color: #666;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		margin-bottom: 6px;
	}

	& .feature-item {
		display: flex;
		align-items: flex-start;
		gap: 6px;
		margin-bottom: 6px;
		font-size: 13px;
		line-height: 1.5;

		& .icon {
			flex-shrink: 0;
			margin-top: 2px;

			&.check {
				color: #4caf50;
			}

			&.close {
				color: #e0e0e0;
			}
		}

		& .feature-text {
			color: #444;

			&.not-included {
				color: #999;
				text-decoration: line-through;
			}
		}

		& .feature-value {
			font-weight: 600;
			color: #1a1a1a;
		}
	}

	& .plan-action {
		margin-top: auto;
	}

	& .action-button {
		width: 100%;
		height: 40px;
		border-radius: 8px;
		border: none;
		font-size: 14px;
		font-weight: 600;
		cursor: pointer;
		transition: all 0.2s ease;
		display: flex;
		align-items: center;
		justify-content: center;

		&.primary {
			background: var(--mu-primary-dark);
			background: linear-gradient(to right, var(--mu-primary-dark), var(--mu-primary-light));
			color: white;

			&:hover {
				transform: translateY(-1px);
				box-shadow: 0 4px 12px rgba(23, 103, 193, 0.3);
			}

			&:active {
				transform: translateY(0);
			}
		}

		&.secondary {
			background: #f4f4f4;
			color: var(--mu-primary-dark);
			border: 1px solid #e0e0e0;

			&:hover {
				background: #e8e8e8;
				border-color: var(--mu-primary-main);
			}
		}

		&.current {
			background: #e8e8e8;
			color: #666;
			cursor: default;
			border: 1px solid #d0d0d0;

			&:hover {
				transform: none;
				box-shadow: none;
			}
		}

		&.contact {
			background: #2c2c2c;
			color: white;

			&:hover {
				background: #1a1a1a;
			}
		}
	}

	& .settings-btn {
		display:flex;
		gap: 10px;
	}

	& .expiry-dates {
		padding: 4px 0px;
	}

	& .trial-cancelled-text {
		display: flex;
		align-items: center;
		padding-top: 6px;
		color: var(--mu-error-main);
		font-size: 14px;
		font-weight: 500;
	}

	& .upgrade {
		grid-area: upgrade;
		display: flex;
		justify-content: start;
		padding-top: 6px;
		margin-right:6px;

		& .upgrade-btn {
			cursor: pointer;
			height: 28px;
			width: 96px;
			font-size: 14px;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: center;
			background: var(--mu-primary-dark);
			background: -webkit-linear-gradient(to right, var(--mu-primary-dark), var(--mu-primary-light));
			background: linear-gradient(to right, var(--mu-primary-dark), var(--mu-primary-light));
			color: white;
			border-radius: 30px;
			font-weight: 500;
			position: relative;

			& .upgrade-text {
				display: flex;
				align-items: center;
				justify-content: center;
				margin-left: -5px;
			}

			& svg {
				font-size:20px;
				margin-right: 3px;
			}

			&.cancel-trial {
				background: var(--mu-error-dark);
				background: -webkit-linear-gradient(to right, var(--mu-error-dark), var(--mu-error-light));
				background: linear-gradient(to right, var(--mu-error-dark), var(--mu-error-light));
				width: 110px;
			}

			&.disabled {
				opacity: 0.6;
				cursor: not-allowed;
			}

			& .spinner-wrapper {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				width: 20px;
				height: 20px;
				margin-right: 3px;
			}
		}

		&.muted {
			& .upgrade-btn {
				background: #e2ebf3;
				color:var(--mu-primary-dark);
				& svg {
					font-size:16px;
				}
			}
		}

		&.get-pro {
			padding-top: 0px;
			& .upgrade-btn {
				width: 132px;
			}
		}
	}

	& .upgrade-to-next-tier {
		background-color: #e2ebf3;
		height: auto;
		border-radius: 12px;
		margin-top: 18px;
		padding:12px;
		display:grid;
		grid-template-rows: 42px auto;
		grid-template-columns: 1fr 140px;
		grid-template-areas:
			'title button'
			'text  text';

		& .title {
			grid-area:title;
		}
		& .button {
			grid-area:button;
		}
		& .text {
			grid-area:text;
		}
	}
`

const SET_AUTO_RENEW = gql`
mutation set_auto_renew($auto_renew: Boolean) {
	user_update(plan_auto_renew: $auto_renew) {
		data{
			id
			first_name
			last_name
			email
			default_tower_id
			metadata
			phone {
				code
				number
			}
			organizations {
				plan
				plan_purchase_date
				plan_expiry_date
				plan_auto_renew
				go_weather_credit
			}
		}
	}
}`

const PlanButton = ({ ...props }) => {
	const history = useHistory()
	const handle_click = () => {
		if (props.disabled || props.loading) {
			return
		}
		if (props.onClick) {
			props.onClick()
		} else if (props.link) {
			history.push(props.link)
		}
	}
	return (<div className={"upgrade" + (props.muted ? ' muted' : '')}>
		<div
			className={`upgrade-btn${props.variant === 'cancel-trial' ? ' cancel-trial' : ''}${props.disabled || props.loading ? ' disabled' : ''}`}
			onClick={handle_click}
		>
			<div className="upgrade-text">
				{props.loading ? (
					<span className="spinner-wrapper">
						<CircularProgress
							size={16}
							thickness={4}
							sx={{
								color: 'white',
								'& svg': {
									marginRight: '0px !important'
								}
							}}
						/>
					</span>
				) : props.icon}
				{props.loading ? 'Processing...' : props.label}
			</div>
		</div>
	</div>)
}

const Plan = () => {
	const head = <Helmet>
		<title>Wand - Plan</title>
	</Helmet>

	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)

	// Example: Get a specific search parameter
	const upgraded = searchParams.get('upgraded')
	if (upgraded)
		window.location.href = SETTINGS_PLAN_PATH

	const user_op = useUser()
	const tower_op = useTowers()
	const members_op = useMembers()
	const [update_auto_renew, { loading: auto_renew_loading }] = useMutation(SET_AUTO_RENEW)
	const [present_alert] = useIonAlert()

	const toast = new Toast(...useIonToast(), { duration:5000 })

	// Count only self-toggled towers (where self===true AND org_ids is empty)
	// Team-toggled towers don't count against personal quota
	const towers_used = (tower_op?.data?.towers||[]).filter((t:any) => {
		const toggle_data = t?.twenty_four_hrs_forecast_toggled
		const org_ids = toggle_data?.org_ids
		const is_org_ids_empty = !org_ids || !Array.isArray(org_ids) || org_ids.length === 0
		return toggle_data?.self === true && is_org_ids_empty
	}).length

	const members_invited = (members_op?.data?.members||[]).length

	// const [plan, set_plan] = useState({})
	const history = useHistory()
	const theme = useTheme()
	const css_vars:any = {
		'--mu-primary-light': theme.palette.primary.light,
		'--mu-primary-dark': theme.palette.primary.dark,
		'--mu-primary-main': theme.palette.primary.main,
		'--mu-error-light': theme.palette.error.light,
		'--mu-error-dark': theme.palette.error.dark,
		'--mu-error-main': theme.palette.error.main
	}
	const redirect = false

	// Calculate trial status
	const now = new Date()
	const is_trial_active = user_op.data?.trial && !user_op.data?.trial_cancelled && user_op.data?.trial_expiry_date && user_op.data.trial_expiry_date > now
	const is_trial_cancelled = user_op.data?.trial && user_op.data?.trial_cancelled && user_op.data?.trial_expiry_date && user_op.data.trial_expiry_date > now
	const trial_days_remaining = user_op.data?.trial_expiry_date
		? Math.ceil((user_op.data.trial_expiry_date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
		: 0

	// store.set('plan', 'free', true)
	// store.get('plan', plan_name => {
	// 	if (plan_name && plan.name != plan_name)
	// 		set_plan(get_plan(plan_name))
	// })

	// console.log({
	// 	plan,
	// 	data: user_op.data
	// })

	const set_auto_renew = (auto_renew:any) => {
		update_auto_renew({
			variables:{ auto_renew },
			onCompleted:(data:any) => {
				const ae = (data?.user_update?.data?.organizations||[])[0]?.plan_auto_renew
				toast.show(`Plan auto-renewal successfully turned ${ae ? 'on' : 'off'}`)
			},
			onError: (err:any) => {
				console.error(err)
				toast.show('Oops, an error happened on our end. Please try again later.', { error:true, closeText:'close' })
			}
		})
	}

	const cancel_trial = () => {
		update_auto_renew({
			variables: { auto_renew: false },
			onCompleted: (data:any) => {
				// Refetch user data to get updated trial status
				user_op.refetch().then(() => {
					// Show success alert
					present_alert({
						header: 'Trial Cancelled',
						message: 'Your trial has been cancelled and no charges will be incurred at the end of the free trial.',
						buttons: ['OK']
					})
				})
			},
			onError: (err:any) => {
				console.error(err)
				toast.show('Failed to cancel trial. Please try again later.', { error:true, closeText:'close' })
			}
		})
	}

	const get_feature_data = (plan_name:string) => {
		switch (plan_name) {
		case 'free':
			return {
				towers: { value: '3', text: 'closest towers only' },
				forecast: { value: '2 hours', text: 'hazard forecast' },
				history: { value: '48 hours', text: 'weather history' },
				team: { value: '0', text: 'team members' },
				credit: null,
				export: false
			}
		case 'basic':
			return {
				towers: { value: '10', text: 'towers anywhere' },
				forecast: { value: '24 hours', text: 'hazard forecast' },
				history: { value: 'Unlimited', text: 'weather history' },
				team: { value: '0', text: 'team members' },
				credit: { value: 'AU$100', text: 'GoWeather credit' },
				export: true
			}
		case 'professional':
			return {
				towers: { value: '10', text: 'towers anywhere' },
				forecast: { value: '24 hours', text: 'hazard forecast' },
				history: { value: 'Unlimited', text: 'weather history' },
				team: { value: '5', text: 'team members' },
				credit: { value: 'AU$300', text: 'GoWeather credit' },
				export: true
			}
		case 'organization':
			return {
				towers: { value: 'Unlimited', text: 'towers' },
				forecast: { value: '24 hours', text: 'hazard forecast' },
				history: { value: 'Unlimited', text: 'weather history' },
				team: { value: '20+', text: 'team members' },
				credit: { value: 'AU$500', text: 'GoWeather credit' },
				export: true
			}
		default:
			return null
		}
	}

	const get_plan_tier = (plan_name:string) => {
		const tiers:any = { free: 0, basic: 1, professional: 2, organization: 3 }
		return tiers[plan_name] || 0
	}

	const handle_upgrade = (plan_name:string) => {
		if (plan_name === (user_op.data?.plan?.name || 'free')) return
		history.push(`${SETTINGS_UPGRADE_PLAN_PATH}?plan=${plan_name}`)
	}

	// Map plan name to plan key (e.g., 'solo' -> 'basic')
	const get_plan_key = (plan_name:string) => {
		const name_to_key:any = { 'solo': 'basic', 'organisation': 'organization', 'organization': 'organization' }
		return name_to_key[plan_name] || plan_name
	}

	const current_plan = get_plan_key(user_op.data?.plan?.name || 'free')

	// Detect if mobile (< 768px)
	const [is_mobile, set_is_mobile] = useState(window.innerWidth < 768)

	useEffect(() => {
		const handle_resize = () => {
			set_is_mobile(window.innerWidth < 768)
		}

		window.addEventListener('resize', handle_resize)
		return () => window.removeEventListener('resize', handle_resize)
	}, [])

	// Mobile-optimized plan order based on current plan
	const get_mobile_plan_order = (current:string) => {
		if (current === 'free') {
			return ['basic', 'free', 'professional', 'organization']
		} else if (current === 'basic') {
			return ['professional', 'basic', 'organization', 'free']
		} else if (current === 'professional') {
			return ['organization', 'professional', 'basic', 'free']
		} else if (current === 'organization') {
			return ['organization', 'basic', 'professional', 'free']
		}
		return ['free', 'basic', 'professional', 'organization']
	}

	// Desktop order: always Free, Solo, Pro, Org
	const desktop_order = ['free', 'basic', 'professional', 'organization']

	const plan_order = is_mobile ? get_mobile_plan_order(current_plan) : desktop_order
	const plans = plan_order.map(key => ({ key, data: get_plan(key) }))

	return redirect ? <Redirect to={LOGIN_PATH} /> :(
		<div>
			<IonPage>
				{head}
				<TitleHeader title="Your Plan" onBack={() => history.push(SETTINGS_PATH)} />
				<IonContent fullscreen>
					<div style={css_vars}>{user_op.loading ? null :
						<MasterDiv>
							<div className="title-header">
								<Typography variant="h5" component="h2" className="top-title">
									{user_op.data?.plan?.name}{user_op.data?.trial ? ' (Free Trial)' : ''}
								</Typography>{!is_paid(user_op.data?.plan?.name) || is_trial_active || is_trial_cancelled ? null :
								<FormControlLabel
									value="start"
										control={<Switch
										color="primary"
										defaultChecked={user_op.data?.plan_auto_renew}
										onChange={(e:any) => set_auto_renew(e.target.checked)} />}
									label={<Typography variant="body2" color="primary">Auto-renew</Typography>}
									labelPlacement="start"
									className="h-icon"
								/>}
							</div>{is_trial_active ?
							<div className="expiry-dates">
								<Typography variant="caption" color="text.secondary" className="text">
									Trial expires in {trial_days_remaining} day{trial_days_remaining !== 1 ? 's' : ''} after which you'll be charged ${user_op.data?.plan?.price}
								</Typography>
							</div> : is_trial_cancelled ?
							<div className="expiry-dates">
								<Typography variant="caption" color="text.secondary" className="text">
									Trial expires in {trial_days_remaining} day{trial_days_remaining !== 1 ? 's' : ''}, after which your account will revert to Free
								</Typography>
							</div> : is_paid(user_op.data?.plan?.name) ?
							<div className="expiry-dates">
								<Typography variant="caption" color="text.secondary" className="text">
									Yearly - Next payment on {user_op.data?.plan_expiry_date_label}
								</Typography>
							</div> : null}
							<div className="settings-btn">{is_upgradable(current_plan) ?
								<PlanButton
									link={`${SETTINGS_UPGRADE_PLAN_PATH}?plan=${user_op.data?.plan?.next}`}
									icon={<UpgradeIcon/>}
									label="Upgrade"/> : null}
									{is_trial_active ?
								<PlanButton
									onClick={cancel_trial}
									variant="cancel-trial"
									icon={<CancelIcon/>}
									label="Cancel Trial"
									loading={auto_renew_loading}
									disabled={auto_renew_loading}/> : null}
									{is_trial_cancelled ?
								<div className="trial-cancelled-text">
									Trial cancelled
								</div> : null}
									{/*{is_paid(user_op.data?.plan?.name) ?
								<PlanButton
									link={SETTINGS_MANAGE_PLAN_PATH}
									icon={<SettingsIcon/>}
									muted={true}
									label="Manage"/> : null}*/}
							</div>

							{user_op.data?.plan?.upgrade_ad ?
							<div className="upgrade-to-next-tier">
								<Typography variant="h6" component="h2" className="title" sx={{ fontSize:'1rem', paddingBottom:'8px' }}>
									{user_op.data?.plan?.upgrade_ad?.name}
								</Typography>
								<div className="upgrade button get-pro">
									<div className="upgrade-btn" onClick={() => history.push(`${SETTINGS_UPGRADE_PLAN_PATH}?plan=${user_op.data?.plan?.next}`)}>
										<div className="upgrade-text">
											{user_op.data?.plan.upgrade_ad.cta}
										</div>
									</div>
								</div>
								<Typography variant="caption" color="text.secondary" className="text">
									{user_op.data?.plan?.name === 'free'
										? user_op.data?.plan?.upgrade_ad?.message
										: user_op.data?.plan?.upgrade_ad?.message.replace('Start with a 14-day free trial.', '').trim()}
								</Typography>
							</div> : null }

							<div className="all-plans-section">
								<Typography className="section-title">
									All Plans
								</Typography>
								<Typography className="intro-text">
									Choose the plan that best fits your needs. All paid plans include GoWeather credit.
								</Typography>

								<div className="comparison-container">
									{plans.map(({ key, data }) => {
										const is_current = key === current_plan
										const features = get_feature_data(key)

										return (
											<div key={key} className={`plan-card${is_current ? ' current-plan' : ''}`}>
												{is_current && (
													<div className="current-badge">
														<StarIcon />
														Current
													</div>
												)}

												<div className="plan-header">
													<Typography className="plan-name">{
														data?.name == 'organization' ? 'organisation' :
														data?.name == 'solo' ? 'solo - 1 user' :
														data?.name == 'professional' ? 'professional - 5 users' :
														data?.name
													}</Typography>
													{key === 'free' ? (
														<Typography className="plan-price free">Free Forever</Typography>
													) : key === 'organization' ? (
														<Typography className="plan-price contact">Contact us</Typography>
													) : (
														<>
															<Typography className="plan-price">
																<span className="currency">AU$</span>
																{data?.price}
																<span className="period"> /year</span>
															</Typography>
															<Typography className="plan-gst">+ GST</Typography>
															{data?.trial_days && current_plan === 'free' && (
																<Typography className="plan-gst" sx={{ color: '#4caf50', fontWeight: 600, fontStyle: 'normal', marginTop: '4px' }}>
																	Start with {data.trial_days}-day free trial
																</Typography>
															)}
														</>
													)}
												</div>

												<div className="plan-features">
													<div className="feature-group">
														<Typography className="feature-group-title">Core Features</Typography>
														<div className="feature-item">
															<CheckIcon className="icon check" fontSize="small" />
															<Typography className="feature-text">
																<span className="feature-value">{features?.towers.value}</span> {features?.towers.text}
															</Typography>
														</div>
														<div className="feature-item">
															<CheckIcon className="icon check" fontSize="small" />
															<Typography className="feature-text">
																<span className="feature-value">{features?.forecast.value}</span> {features?.forecast.text}
															</Typography>
														</div>
														<div className="feature-item">
															<CheckIcon className="icon check" fontSize="small" />
															<Typography className="feature-text">
																<span className="feature-value">{features?.history.value}</span> {features?.history.text}
															</Typography>
														</div>
													</div>

													<div className="feature-group">
														<Typography className="feature-group-title">Collaboration</Typography>
														{features?.team.value === '0' ? (
															<div className="feature-item">
																<CheckIcon className="icon check" fontSize="small" />
																<Typography className="feature-text">
																	Just You
																</Typography>
															</div>
														) : (
															<div className="feature-item">
																<CheckIcon className="icon check" fontSize="small" />
																<Typography className="feature-text">
																	<span className="feature-value">{features?.team.value}</span> {features?.team.text}
																</Typography>
															</div>
														)}
													</div>

													{features?.credit && (
														<div className="feature-group">
															<Typography className="feature-group-title">Bonus</Typography>
															<div className="feature-item">
																<CheckIcon className="icon check" fontSize="small" />
																<Typography className="feature-text">
																	<span className="feature-value">{features?.credit.value}</span> {features?.credit.text}
																</Typography>
															</div>
														</div>
													)}

													<div className="feature-group">
														<Typography className="feature-group-title">Data</Typography>
														<div className="feature-item">
															{features?.export ? (
																<>
																	<CheckIcon className="icon check" fontSize="small" />
																	<Typography className="feature-text">
																		Data export (CSV, Excel)
																	</Typography>
																</>
															) : (
																<>
																	<CloseIcon className="icon close" fontSize="small" />
																	<Typography className="feature-text not-included">
																		Data export
																	</Typography>
																</>
															)}
														</div>
													</div>
												</div>

												<div className="plan-action">
													{is_current ? (
														<button className="action-button current">
															Current Plan
														</button>
													) : key === 'organization' ? (
														<button
															className="action-button contact"
															onClick={() => handle_upgrade(key)}>
															Contact Us
														</button>
													) : (() => {
														const current_tier = get_plan_tier(current_plan)
														const compare_tier = get_plan_tier(key)
														const is_upgrade = compare_tier > current_tier

														if (!is_upgrade) {
															return null
														}

														return (
															<button
																className={`action-button ${current_plan === 'free' && key === 'basic' ? 'primary' : 'secondary'}`}
																onClick={() => handle_upgrade(key)}>
																{current_plan === 'free' && key === 'basic' ? 'Get Started' : 'Upgrade'}
															</button>
														)
													})()}
												</div>
											</div>
										)
									})}
								</div>
							</div>

							<DescriptionBox
								title="Your limits"
								items={user_op.data?.plan?.limits}
								members={members_invited}
								towers={towers_used}
								go_weather_credit={user_op.data?.go_weather_credit}
							/>
							<DescriptionBox
								title="Your benefits"
								items={user_op.data?.plan?.benefits}
								no_icon_color={true}
							/>
						</MasterDiv>}
					</div>
				</IonContent>
			</IonPage>
		</div>
	)
}

export default Plan